import { useState } from 'react';

const faqs = [
  {
    question: 'How much does solar energy cost in Australia?',
    answer: 'A standard 6.6kW residential solar system in Australia typically costs between $5,000 and $8,500 after the federal STC rebate is applied. Larger systems (10kW–13.3kW) range from $8,000 to $13,000. Commercial systems are priced per kW and benefit from significant economies of scale. AK Energy helps you compare quotes from multiple verified providers to ensure you get the best value.',
  },
  {
    question: 'What solar energy solutions are available in Australia?',
    answer: 'Australia has access to a full range of solar energy solutions including grid-connected rooftop solar for homes and businesses, off-grid solar systems for rural properties, solar battery storage (brands include Tesla Powerwall, SolarEdge, Sungrow and LG Chem), solar hot water systems, community solar projects, and commercial ground-mount and carport installations.',
  },
  {
    question: 'How do I find trusted renewable energy providers in Australia?',
    answer: "AK Energy is Australia's renewable energy marketplace, connecting customers with verified solar installers and providers. All providers listed on AK Energy are CEC-accredited (Clean Energy Council), ensuring they meet Australia's national quality and safety standards. You can compare providers by location, reviews, system types, and certifications.",
  },
  {
    question: 'Is solar energy suitable for businesses in Australia?',
    answer: "Commercial solar is one of the best investments Australian businesses can make. With payback periods of 3–5 years and system lifespans of 25+ years, commercial solar delivers decades of financial returns. Businesses can also claim immediate tax deductions under the instant asset write-off scheme, further improving ROI. Solar also supports sustainability reporting and ESG goals, which is increasingly important to customers, investors, and supply chains.",
  },
  {
    question: 'How much can I save with solar panels in Australia?',
    answer: "Australian homeowners save an average of $1,200–$2,000 per year with a standard 6.6kW solar system. Savings depend on your daily energy consumption, the feed-in tariff rate in your state, system size, and orientation. Adding battery storage can increase savings by a further $500–$1,000 per year by eliminating evening grid usage. Our solar calculator provides a personalised estimate based on your location and usage.",
  },
  {
    question: 'What is the best time to install solar in Australia?',
    answer: "There is no bad time to install solar in Australia — every season generates meaningful electricity given Australia's solar resources. However, installing before summer maximises your first-year savings due to longer daylight hours and higher solar intensity. Government rebates and STC values are also subject to change, so earlier installation can lock in current incentive levels.",
  },
  {
    question: 'What happens to solar panels on cloudy days?',
    answer: 'Solar panels still generate electricity on cloudy and overcast days — typically at 10–25% of their rated capacity. Australia has relatively few fully overcast days compared to most countries, and even diffuse light generates meaningful power. Your system is grid-connected, meaning any shortfall on cloudy days is automatically topped up from the grid at your retail electricity rate.',
  },
  {
    question: 'Can I add battery storage to an existing solar system?',
    answer: "Yes — most modern solar inverters are battery-ready and can be retrofitted with battery storage. The main considerations are inverter compatibility, available wall space for the battery unit, and switchboard capacity. AK Energy's providers can assess your existing system and recommend the most compatible battery option, whether that's a Tesla Powerwall, SolarEdge or another leading brand.",
  },
];

export default function SolarFAQ() {
  const [openIdx, setOpenIdx] = useState<number | null>(0);

  const toggle = (idx: number) => {
    setOpenIdx(openIdx === idx ? null : idx);
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: f.answer,
      },
    })),
  };

  return (
    <section className="bg-background-50 py-20 md:py-28">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-12 lg:gap-16 items-start">
          {/* Left column */}
          <div className="lg:sticky lg:top-28">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-50 border border-primary-200/50 mb-5">
              <i className="ri-question-answer-line text-primary-500 text-sm"></i>
              <span className="text-xs font-semibold text-primary-700 tracking-wide uppercase whitespace-nowrap">FAQ</span>
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-extrabold text-foreground-950 mb-4">
              Solar Energy<br />Questions<br />Answered
            </h2>
            <p className="text-base text-foreground-600 leading-relaxed mb-8">
              Everything you need to know about solar energy solutions in Australia — from costs and rebates to installation and savings.
            </p>
            <a
              href="/#contact"
              className="whitespace-nowrap inline-flex items-center gap-2 px-5 py-3 text-sm font-semibold rounded-xl border-2 border-foreground-200 text-foreground-800 hover:border-foreground-400 hover:bg-foreground-50 transition-all duration-200 cursor-pointer"
            >
              Ask a Question
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>

          {/* FAQ list */}
          <div className="space-y-3">
            {faqs.map((faq, idx) => (
              <div
                key={idx}
                className={`rounded-xl border transition-all duration-200 overflow-hidden ${
                  openIdx === idx
                    ? 'bg-primary-50 border-primary-200'
                    : 'bg-background-100/70 border-background-200/70 hover:border-background-300'
                }`}
              >
                <button
                  onClick={() => toggle(idx)}
                  className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left cursor-pointer"
                >
                  <span className={`text-sm font-semibold leading-snug ${openIdx === idx ? 'text-primary-700' : 'text-foreground-900'}`}>
                    {faq.question}
                  </span>
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 transition-all duration-200 ${
                    openIdx === idx ? 'bg-primary-500 text-white' : 'bg-background-200 text-foreground-600'
                  }`}>
                    <i className={`text-sm transition-transform duration-200 ${openIdx === idx ? 'ri-subtract-line' : 'ri-add-line'}`}></i>
                  </div>
                </button>
                {openIdx === idx && (
                  <div className="px-5 pb-5">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}